import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import { getStudentModuleTestSession } from "@/services/admin/student.service";
import type { ModuleTestSessionAdmin } from "@/services/admin/common.interfaces";
import {
  buildModuleTestTimeline,
  buildModuleTestPerformance,
  type ModuleTestTimelineEvent,
  type ModuleTestPerformanceItem,
} from "@/services/admin/moduleTestReportBuilders";

const BackLink: React.FC<{ studentId: string; packageId: string }> = ({ studentId, packageId }) => {
  const navigate = useNavigate();
  const to = `/admin/access/students/${studentId}/packages/${packageId}`;
  return (
    <div className="mb-6">
      <button
        type="button"
        onClick={() => navigate(to)}
        className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
      >
        <i className="fa-solid fa-arrow-left" aria-hidden />
        <span>Voltar ao pacote</span>
      </button>
    </div>
  );
};

const TimelineEventRow: React.FC<{
  event: ModuleTestTimelineEvent;
  isLast: boolean;
}> = ({ event, isLast }) => {
  const at = new Date(event.at).toLocaleString();
  const iconColor =
    event.type === "TestCompleted"
      ? "text-amber-600 bg-amber-50"
      : event.type === "AnswerSubmitted"
        ? event.isCorrect
          ? "text-green-600 bg-green-50"
          : "text-red-600 bg-red-50"
        : "text-neutral-600 bg-neutral-100";

  const content = (() => {
    switch (event.type) {
      case "SessionStarted":
        return <span className="font-medium">Sessão iniciada</span>;
      case "AnswerSubmitted": {
        const showMc =
          event.detail?.options != null && event.detail.options.length > 0;
        return (
          <>
            <span className="font-medium">Resposta #{event.sequence}</span>
            <span
              className={
                event.isCorrect
                  ? "inline-flex items-center rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800"
                  : "inline-flex items-center rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800"
              }
            >
              {event.isCorrect ? "Correta" : "Incorreta"}
            </span>
            {showMc && (
              <div className="basis-full mt-2 space-y-2 rounded border bg-neutral-50 p-3">
                {(event.detail?.exerciseTitle || event.detail?.questionText) && (
                  <div>
                    {(() => {
                      const t = event.detail?.exerciseTitle?.trim() ?? "";
                      const q = event.detail?.questionText?.trim() ?? "";
                      if (t && q && t === q) {
                        return <p className="text-sm font-medium text-neutral-800">{q}</p>;
                      }
                      if (t && q) {
                        return (
                          <>
                            <p className="text-sm font-medium text-neutral-700">{t}</p>
                            <p className="mt-1 text-sm font-medium text-neutral-800">{q}</p>
                          </>
                        );
                      }
                      if (t) {
                        return <p className="text-sm font-medium text-neutral-800">{t}</p>;
                      }
                      if (q) {
                        return <p className="text-sm font-medium text-neutral-800">{q}</p>;
                      }
                      return null;
                    })()}
                  </div>
                )}
                <div className="space-y-1">
                  {event.detail!.options.map((option) => {
                    const selectedAndCorrect = option.isSelected && event.isCorrect;
                    const selectedAndIncorrect = option.isSelected && !event.isCorrect;
                    return (
                      <div
                        key={`${event.exerciseId}-${option.order}-${option.text}`}
                        className={`rounded border-2 p-2 text-xs ${
                          selectedAndCorrect
                            ? "border-green-500 bg-green-200 font-semibold"
                            : selectedAndIncorrect
                              ? "border-red-500 bg-red-200 font-semibold"
                              : option.isCorrect && !option.isSelected
                                ? "border-green-300 bg-green-50"
                                : "border-neutral-200 bg-white"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{option.order}.</span>
                          <span className="break-words">{option.text}</span>
                          {selectedAndCorrect && (
                            <span className="ml-auto text-xs font-bold text-green-800">✓ Selecionada e correta</span>
                          )}
                          {selectedAndIncorrect && (
                            <span className="ml-auto text-xs font-bold text-red-800">✗ Selecionada e incorreta</span>
                          )}
                          {!option.isSelected && option.isCorrect && (
                            <span className="ml-auto text-xs font-medium text-green-700">✓ Resposta correta</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        );
      }
      case "TestCompleted":
        return (
          <>
            <span className="font-medium">Teste concluído</span>
            {event.scorePercent != null && (
              <span className="text-neutral-600 ml-2">
                {event.scorePercent.toFixed(1)}%
                {event.passed != null && (event.passed ? " — Aprovado" : " — Reprovado")}
              </span>
            )}
          </>
        );
      default:
        return null;
    }
  })();

  const icon =
    event.type === "SessionStarted"
      ? "fa-play"
      : event.type === "AnswerSubmitted"
        ? "fa-circle-check"
        : "fa-trophy";

  return (
    <div className="relative flex gap-4 py-4">
      <div
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${iconColor}`}
        aria-hidden
      >
        <i className={`fa-solid ${icon} text-sm`} />
      </div>
      <div className={`flex-1 min-w-0 pt-0.5 ${!isLast ? "border-b border-neutral-100 pb-4" : ""}`}>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">{content}</div>
        <p className="mt-1 text-xs text-neutral-500">{at}</p>
      </div>
    </div>
  );
};

const PerformanceRow: React.FC<{ item: ModuleTestPerformanceItem }> = ({ item }) => (
  <div className="flex items-center justify-between gap-4 rounded-lg border border-neutral-200 bg-white p-3">
    <div className="min-w-0 flex-1">
      <span className="text-sm font-medium text-neutral-900">
        {item.title || `Questão ${item.sequence}`}
      </span>
      <p className="text-xs text-neutral-500 mt-0.5">
        {new Date(item.answeredAt).toLocaleString()}
      </p>
    </div>
    <span
      className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
        item.isCorrect ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
      }`}
    >
      {item.isCorrect ? "Correta" : "Incorreta"}
    </span>
  </div>
);

const SessionCard: React.FC<{
  session: ModuleTestSessionAdmin;
  /** From route: highlights which unlock row is for the package being viewed. */
  currentPackageId?: string;
}> = ({ session, currentPackageId }) => {
  const [activeTab, setActiveTab] = useState<"timeline" | "performance">("timeline");
  const timelineEvents = buildModuleTestTimeline(session);
  const performanceItems = buildModuleTestPerformance(session);
  const correctCount = session.answers.filter((a) => a.isCorrect).length;
  const totalCount = session.answers.length;
  const infoScore =
    session.scorePercent != null ? (
      <p className="text-sm font-medium">{session.scorePercent.toFixed(1)}%</p>
    ) : !session.finished ? (
      <p className="text-sm font-medium">— (em andamento)</p>
    ) : (
      <p className="text-sm font-medium">—</p>
    );

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900">Informações da sessão</h2>
          <span
            className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
              session.finished
                ? session.passed
                  ? "bg-green-100 text-green-800"
                  : "bg-amber-100 text-amber-800"
                : "bg-blue-100 text-blue-800"
            }`}
          >
            {session.finished
              ? session.passed
                ? "Concluído — Aprovado"
                : "Concluído — Reprovado"
              : "Em andamento"}
          </span>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <p className="text-sm text-neutral-500">Iniciado em</p>
            <p className="text-sm font-medium">{new Date(session.createdAt).toLocaleString()}</p>
          </div>
          {session.completedAt && (
            <div>
              <p className="text-sm text-neutral-500">Concluído em</p>
              <p className="text-sm font-medium">
                {new Date(session.completedAt).toLocaleString()}
              </p>
            </div>
          )}
          <div>
            <p className="text-sm text-neutral-500">Pontuação</p>
            {infoScore}
          </div>
          <div>
            <p className="text-sm text-neutral-500">Respostas</p>
            <p className="text-sm font-medium">
              {correctCount} corretas / {totalCount} total
            </p>
          </div>
        </div>
      </div>

      {session.finished && session.passed === true && (session.unlockedAfterPass?.length ?? 0) > 0 && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950">
          <p className="font-medium">Desbloqueio após aprovação</p>
          <p className="mt-1 text-emerald-900">
            A nota mínima foi atingida; o próximo módulo na ordem do pacote foi (ou pôde ser) desbloqueado no sistema, como
            ao concluir o teste no app do estudante.
          </p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            {session.unlockedAfterPass!.map((u) => {
              const inThisPackage = Boolean(currentPackageId && u.packageId === currentPackageId);
              return (
                <li key={`${u.packageId}-${u.moduleId}`}>
                  <span className="font-medium text-emerald-950">{u.moduleTitle || u.moduleId}</span>
                  {inThisPackage ? (
                    <span className="ml-1 text-xs text-emerald-800">(neste pacote)</span>
                  ) : u.packageName ? (
                    <span className="ml-1 text-xs text-emerald-800">— {u.packageName}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {!session.finished && (
        <div className="rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-950">
          <p className="font-medium">Teste em andamento</p>
          <p className="mt-1 text-sky-900">
            O estudante ainda não finalizou o teste deste módulo. A linha do tempo e o desempenho refletem o que foi
            respondido até agora; pontuação e aprovação aparecem após a conclusão.
          </p>
        </div>
      )}

      <div className="rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex flex-col gap-3 border-b border-neutral-100 pb-4 sm:flex-row sm:items-end sm:justify-between">
          <h3 className="text-base font-semibold text-neutral-900">Detalhes da sessão</h3>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-neutral-100 px-2.5 py-1 font-medium text-neutral-700">
              {totalCount} respostas
            </span>
            <span className="rounded-full bg-neutral-100 px-2.5 py-1 font-medium text-neutral-700">
              {timelineEvents.length} eventos na linha do tempo
            </span>
            <span className="rounded-full bg-neutral-100 px-2.5 py-1 font-medium text-neutral-700">
              {correctCount} corretas
            </span>
          </div>
        </div>
        <div
          role="tablist"
          aria-label="Abas do relatório"
          className="mb-5 grid h-10 w-full max-w-2xl grid-cols-2 rounded-md bg-neutral-100 p-1"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "timeline"}
            aria-controls="module-test-timeline-panel"
            onClick={() => setActiveTab("timeline")}
            className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 ${
              activeTab === "timeline" ? "bg-white text-neutral-950 shadow-sm" : "text-neutral-500 hover:text-neutral-700"
            }`}
          >
            <i className="fa-solid fa-history text-sm" aria-hidden />
            Linha do tempo
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "performance"}
            aria-controls="module-test-performance-panel"
            onClick={() => setActiveTab("performance")}
            className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 ${
              activeTab === "performance"
                ? "bg-white text-neutral-950 shadow-sm"
                : "text-neutral-500 hover:text-neutral-700"
            }`}
          >
            <i className="fa-solid fa-chart-column text-sm" aria-hidden />
            Desempenho
          </button>
        </div>
        {activeTab === "timeline" && (
          <div id="module-test-timeline-panel" role="tabpanel" className="mt-0">
            <div className="relative space-y-0">
              <div className="pointer-events-none absolute bottom-0 left-4 top-0 border-l-2 border-dashed border-neutral-200" />
              {timelineEvents.map((event, index) => (
                <TimelineEventRow
                  key={`${event.type}-${index}`}
                  event={event}
                  isLast={index === timelineEvents.length - 1}
                />
              ))}
            </div>
          </div>
        )}
        {activeTab === "performance" && (
          <div id="module-test-performance-panel" role="tabpanel" className="mt-0 space-y-3">
            {performanceItems.length === 0 ? (
              <p className="py-8 text-center text-sm text-neutral-500">
                Nenhuma resposta registrada
              </p>
            ) : (
              performanceItems.map((item) => (
                <PerformanceRow key={item.exerciseId} item={item} />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const EmptyState: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <div className="rounded-lg border border-neutral-200 bg-white p-12 text-center shadow-sm">
    <i className="fa-solid fa-clipboard-question mb-4 text-4xl text-neutral-400" aria-hidden />
    <h3 className="mb-2 text-lg font-semibold text-neutral-900">
      Nenhuma sessão de teste de módulo encontrada
    </h3>
    <p className="mb-6 text-sm text-neutral-600">
      Este estudante ainda não realizou teste neste módulo ou a sessão não está disponível.
    </p>
    <button
      type="button"
      onClick={onBack}
      className="inline-flex items-center gap-2 rounded-md border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
    >
      <i className="fa-solid fa-arrow-left" aria-hidden />
      Voltar ao pacote
    </button>
  </div>
);

const StudentModuleTestReportPage: React.FC = () => {
  const { studentId, packageId, moduleId } = useParams<{
    studentId: string;
    packageId: string;
    moduleId: string;
  }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<ModuleTestSessionAdmin | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const backToPackage = useCallback(() => {
    if (studentId && packageId) {
      navigate(`/admin/access/students/${studentId}/packages/${packageId}`);
    }
  }, [navigate, studentId, packageId]);

  useEffect(() => {
    if (!studentId || !moduleId) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setNotFound(false);
      try {
        const data = await getStudentModuleTestSession(studentId, moduleId);
        if (!cancelled) {
          setSession(data);
        }
      } catch (err) {
        if (!cancelled) {
          setSession(null);
          setNotFound(true);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId, moduleId]);

  return (
    <AdminPageLayout title="Relatório do teste de módulo">
      <div className="mx-auto max-w-4xl px-4 py-6">
        {studentId && packageId && <BackLink studentId={studentId} packageId={packageId} />}
        {isLoading ? (
          <div className="flex items-center justify-center py-16" aria-busy="true">
            <div
              className="h-12 w-12 animate-spin rounded-full border-2 border-neutral-200 border-t-blue-600"
              aria-label="Carregando"
            />
          </div>
        ) : notFound || !session ? (
          <EmptyState onBack={backToPackage} />
        ) : (
          <SessionCard session={session} currentPackageId={packageId} />
        )}
      </div>
    </AdminPageLayout>
  );
};

export default StudentModuleTestReportPage;
